import type {
  EntityBody,
  SimulationEntityState,
  TransitionRecording,
} from "@cloudnest/kinetic-core-engine";
import { useCallback, useEffect, useRef } from "react";
import type { PieceTypeInitConfig } from "../config/types";
import {
  renderBoard,
  renderEntities,
  renderFlickPreview,
} from "../utils/canvas-renderer";
import type { CanvasLayout } from "../utils/coordinate-transform";
import { resolveAlkagiCanvasTokens } from "../utils/token-bridge";

interface AnimationState {
  readonly recording: TransitionRecording;
  readonly startTime: number;
}

const FRAME_DURATION_MS = 1000 / 60;

export function useCanvasRenderer(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  layout: CanvasLayout | null,
  entities: ReadonlyArray<EntityBody>,
  selectedEntityId: string | null,
  boardCols: number,
  boardRows: number,
  ownerOneId: string,
  pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>,
  flickPreview: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
  } | null,
  animationRecording: TransitionRecording | null,
  onAnimationComplete: () => void,
) {
  const animationRef = useRef<AnimationState | null>(null);
  const animFrameRef = useRef<number>(0);

  const render = useCallback(
    (overrideEntities?: ReadonlyArray<EntityBody>) => {
      const canvas = canvasRef.current;
      if (!canvas || !layout) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Resolve theme tokens once per draw — picks up theme switches without
      // a remount.
      const tokens = resolveAlkagiCanvasTokens(canvas);

      ctx.clearRect(0, 0, layout.canvasWidth, layout.canvasHeight);
      renderBoard(ctx, layout, boardCols, boardRows, tokens);

      renderEntities(
        ctx,
        overrideEntities ?? entities,
        layout,
        selectedEntityId,
        pieceTypesById,
        ownerOneId,
        tokens,
      );

      if (flickPreview) {
        renderFlickPreview(
          ctx,
          flickPreview.fromX,
          flickPreview.fromY,
          flickPreview.toX,
          flickPreview.toY,
          layout,
          tokens,
        );
      }
    },
    [
      canvasRef,
      layout,
      boardCols,
      boardRows,
      entities,
      selectedEntityId,
      pieceTypesById,
      ownerOneId,
      flickPreview,
    ],
  );

  useEffect(() => {
    if (!animationRecording || !layout) {
      animationRef.current = null;
      render();
      return;
    }

    animationRef.current = {
      recording: animationRecording,
      startTime: performance.now(),
    };

    function animate(now: number) {
      const animationState = animationRef.current;
      if (!animationState) return;

      const elapsed = now - animationState.startTime;
      const frameIndex = Math.min(
        Math.floor(elapsed / FRAME_DURATION_MS),
        animationState.recording.frames.length - 1,
      );

      const frame = animationState.recording.frames[frameIndex];
      if (frame) {
        const frameEntities: EntityBody[] = frame.entities.map(
          (frameEntity: SimulationEntityState) => {
            const original = entities.find(
              (entity) => entity.id === frameEntity.id,
            );
            return {
              id: frameEntity.id,
              archetype: original?.archetype ?? "UNKNOWN",
              owner: original?.owner ?? ownerOneId,
              x: frameEntity.x,
              y: frameEntity.y,
              radius: original?.radius ?? 0.35,
              mass: original?.mass ?? 1,
              eliminated: frameEntity.eliminated,
            };
          },
        );

        render(frameEntities);
      }

      if (frameIndex >= animationState.recording.frames.length - 1) {
        animationRef.current = null;
        onAnimationComplete();
        return;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    }

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [
    animationRecording,
    layout,
    entities,
    render,
    onAnimationComplete,
    ownerOneId,
  ]);

  useEffect(() => {
    if (!animationRecording) {
      render();
    }
  }, [render, animationRecording]);
}
